import type {
  Answer,
  SingleQuestion,
  CrossValidationQuestion,
  DimensionId,
  ContextType,
  ContextScore,
  DimensionResult,
  StabilityType,
  OverallStabilityType,
  TypeCode,
  ModeResult,
  CrossValidationResult,
  CrossValidationDetail,
  CrossValidationDimensionCheck,
  ReliabilityLevel,
  DimensionPoleLetter,
  TestResult,
  QuestionnaireConfig,
} from './types';
import { DIMENSION_POLES } from './types';

// 计算单个情境的得分
export function calculateContextScore(
  answers: Answer[],
  questions: SingleQuestion[],
  dimension: DimensionId,
  context: ContextType
): ContextScore {
  const relevantQuestions = questions.filter(
    q => q.dimension === dimension && q.context === context
  );
  
  let poleACount = 0;
  let poleBCount = 0;
  
  for (const question of relevantQuestions) {
    const answer = answers.find(a => a.questionId === question.id);
    if (!answer) continue;
    
    const selectedA = answer.selected === 'A';
    const pointsToPoleA = question.optionADirection === 'A';
    
    if ((selectedA && pointsToPoleA) || (!selectedA && !pointsToPoleA)) {
      poleACount++;
    } else {
      poleBCount++;
    }
  }
  
  const poles = DIMENSION_POLES[dimension];
  const total = poleACount + poleBCount;
  const ratio = total > 0 ? poleACount / poleBCount : 1;
  const isWeak = ratio < 2 && ratio > 0.5;
  
  const resultLetter = poleACount >= poleBCount ? poles.A : poles.B;
  
  // 极点ID映射
  const poleIdMap: Record<DimensionId, { A: string; B: string }> = {
    D1: { A: 'internal', B: 'external' },
    D2: { A: 'systematic', B: 'contextual' },
    D3: { A: 'intrinsic', B: 'extrinsic' },
    D4: { A: 'closure', B: 'exploration' },
  };
  
  const result = poleACount >= poleBCount 
    ? poleIdMap[dimension].A 
    : poleIdMap[dimension].B;
  
  return {
    poleACount,
    poleBCount,
    result,
    resultLetter,
    ratio: `${poleACount}:${poleBCount}`,
    isWeak,
  };
}

// 判定稳定性类型
export function determineStability(
  natural: ContextScore,
  work: ContextScore,
  stress: ContextScore
): StabilityType {
  const n = natural.resultLetter;
  const w = work.resultLetter;
  const s = stress.resultLetter;

  const nWeak = natural.isWeak;
  const wWeak = work.isWeak;
  const sWeak = stress.isWeak;

  // 完全稳定：三层相同
  if (n === w && w === s) return 'stable';

  // 渐进变化判定（基于弱倾向标记）
  // 模式1：自然(强) → 工作(弱，同极) → 应激(反转)
  // 例如：A(强) → A(弱) → B，显示从强A逐渐弱化并最终切换到B
  if (n === w && !nWeak && wWeak && w !== s) {
    return 'gradual';
  }

  // 模式2：自然(弱) → 工作(反转，弱) → 应激(反转，强)
  // 例如：A(弱) → B(弱) → B(强)，显示逐渐强化的反向趋势
  if (nWeak && n !== w && wWeak && w === s && !sWeak) {
    return 'gradual';
  }

  // 模式3：自然(弱) → 工作(同极，弱) → 应激(反转)
  // 例如：A(弱) → A(弱) → B，持续弱化后反转
  if (n === w && nWeak && wWeak && w !== s) {
    return 'gradual';
  }

  // 工作适应：自然≠工作，工作=应激（清晰的切换模式）
  if (n !== w && w === s) return 'work_adaptive';

  // 应激切换：自然=工作，工作≠应激
  if (n === w && w !== s) return 'stress_switch';

  // 模式4：通过比例分析递进趋势（仅当三层结果都不同时）
  // 计算 A极 的强度变化：从自然到工作到应激
  if (n !== w && w !== s && n !== s) {
    const nTotal = natural.poleACount + natural.poleBCount;
    const wTotal = work.poleACount + work.poleBCount;
    const sTotal = stress.poleACount + stress.poleBCount;

    if (nTotal > 0 && wTotal > 0 && sTotal > 0) {
      const nRatio = natural.poleACount / nTotal;
      const wRatio = work.poleACount / wTotal;
      const sRatio = stress.poleACount / sTotal;

      // 检查是否呈单调递增或递减趋势
      const isIncreasing = nRatio < wRatio && wRatio < sRatio;
      const isDecreasing = nRatio > wRatio && wRatio > sRatio;

      if (isIncreasing || isDecreasing) {
        return 'gradual';
      }
    }
  }

  // 其他情况：高弹性
  return 'flexible';
}

// 组合类型编码
export function composeTypeCode(
  d1: string, d2: string, d3: string, d4: string
): TypeCode {
  return `${d1}${d2}${d3}${d4}` as TypeCode;
}

// 计算三层模式
export function calculateModes(dimensions: DimensionResult[]): ModeResult {
  const getLetters = (context: 'natural' | 'work' | 'stress') => {
    const d1 = dimensions.find(d => d.dimensionId === 'D1')![context].resultLetter;
    const d2 = dimensions.find(d => d.dimensionId === 'D2')![context].resultLetter;
    const d3 = dimensions.find(d => d.dimensionId === 'D3')![context].resultLetter;
    const d4 = dimensions.find(d => d.dimensionId === 'D4')![context].resultLetter;
    return composeTypeCode(d1, d2, d3, d4);
  };
  
  return {
    natural: getLetters('natural'),
    work: getLetters('work'),
    stress: getLetters('stress'),
  };
}

// 计算整体稳定性
export function calculateOverallStability(
  dimensions: DimensionResult[]
): {
  type: OverallStabilityType;
  stableAxes: DimensionId[];
  flexibleAxes: { dimension: DimensionId; pattern: StabilityType }[];
} {
  const stableAxes: DimensionId[] = [];
  const flexibleAxes: { dimension: DimensionId; pattern: StabilityType }[] = [];
  
  for (const dim of dimensions) {
    if (dim.stability === 'stable') {
      stableAxes.push(dim.dimensionId);
    } else {
      flexibleAxes.push({ dimension: dim.dimensionId, pattern: dim.stability });
    }
  }
  
  let type: OverallStabilityType;
  if (stableAxes.length === 4) type = 'core_stable';
  else if (stableAxes.length === 3) type = 'single_flex';
  else if (stableAxes.length === 2) type = 'context_adaptive';
  else type = 'highly_flexible';
  
  return { type, stableAxes, flexibleAxes };
}

// 计算交叉验证一致性
export function calculateCrossValidation(
  answers: Answer[],
  questions: CrossValidationQuestion[],
  dimensionResults: DimensionResult[]
): CrossValidationResult {
  const details: CrossValidationDetail[] = [];
  let totalChecks = 0;
  let consistentChecks = 0;

  for (const question of questions) {
    // 找到用户对该题的作答
    const answer = answers.find(a => a.questionId === question.id);
    if (!answer) continue;

    // 找到用户选择的选项
    const selectedOption = question.options.find(opt => opt.id === answer.selected);
    if (!selectedOption) continue;

    // 从 mapping 中提取各维度判定
    const mapping = selectedOption.mapping;
    const dimensionChecks: CrossValidationDimensionCheck[] = [];

    for (const [dimId, cvPole] of Object.entries(mapping)) {
      // 找到单维度判定结果
      const dimResult = dimensionResults.find(d => d.dimensionId === dimId);
      if (!dimResult) continue;

      // 根据题目情境类型选择对比的单维度结果
      let singlePole: string;
      const context = question.context;

      if (context === 'natural') {
        singlePole = dimResult.natural.resultLetter;
      } else if (context === 'work') {
        singlePole = dimResult.work.resultLetter;
      } else if (context === 'stress') {
        singlePole = dimResult.stress.resultLetter;
      } else {
        // mixed 情境：使用主导情境（自然模式）进行对比
        // 如果维度稳定，任意情境结果相同；如果不稳定，使用自然作为基准
        singlePole = dimResult.natural.resultLetter;
      }

      const consistent = cvPole === singlePole;
      dimensionChecks.push({
        dimension: dimId as DimensionId,
        cvResult: cvPole as DimensionPoleLetter,
        singleResult: singlePole,
        consistent,
      });

      totalChecks++;
      if (consistent) consistentChecks++;
    }

    details.push({
      questionId: question.id,
      selectedOption: answer.selected,
      dimensionChecks,
      allConsistent: dimensionChecks.every(c => c.consistent),
    });
  }

  // 计算一致率（百分比）
  const rate = totalChecks > 0 ? (consistentChecks / totalChecks) * 100 : 0;

  // 判定可靠性等级
  let reliable: ReliabilityLevel;
  if (rate >= 80) {
    reliable = 'reliable';
  } else if (rate >= 60) {
    reliable = 'attention_needed';
  } else {
    reliable = 'review_suggested';
  }

  return {
    rate,
    reliable,
    details,
    summary: {
      totalChecks,
      consistentChecks,
      inconsistentChecks: totalChecks - consistentChecks,
    },
  };
}

// 主计算函数
export function calculateResult(
  answers: Answer[],
  config: QuestionnaireConfig,
  responseId: string,
  duration: number,
  participantTag?: string
): TestResult {
  const singleQuestions = config.questions.filter(
    (q): q is SingleQuestion => q.type === 'single'
  );
  const cvQuestions = config.questions.filter(
    (q): q is CrossValidationQuestion => q.type === 'cross_validation'
  );
  
  // 计算各维度结果
  const dimensions: DimensionResult[] = (['D1', 'D2', 'D3', 'D4'] as DimensionId[]).map(dim => {
    const natural = calculateContextScore(answers, singleQuestions, dim, 'natural');
    const work = calculateContextScore(answers, singleQuestions, dim, 'work');
    const stress = calculateContextScore(answers, singleQuestions, dim, 'stress');
    const stability = determineStability(natural, work, stress);
    
    return { dimensionId: dim, natural, work, stress, stability };
  });
  
  // 计算三层模式
  const modes = calculateModes(dimensions);
  
  // 计算整体稳定性
  const { type: overallStability, stableAxes, flexibleAxes } = calculateOverallStability(dimensions);
  
  // 计算交叉验证
  const crossValidation = calculateCrossValidation(answers, cvQuestions, dimensions);
  
  return {
    responseId,
    version: config.version,
    timestamp: new Date().toISOString(),
    duration,
    participantTag,
    answers,
    dimensions,
    modes,
    overallStability,
    stableAxes,
    flexibleAxes,
    crossValidation,
  };
}